import { useEffect, useRef, useState } from "react";

// Voice infra (eva-voice + eva-voice-prewarm + ELEVENLABS_API_KEY + the
// `eva-voice` storage bucket + the `eva_voice_scripts` table) lives on this
// project's Lovable Cloud backend. The shared ElectricSheep project handles
// auth/users/ambassadors only — keep that wiring untouched.
// Hard fallbacks so chips render even if VITE_* env vars are missing at
// build time on the deployed bundle (publishable keys are safe to inline).
const VOICE_SUPABASE_URL =
  (import.meta.env.VITE_SUPABASE_URL as string | undefined) ??
  "https://dvddfvigkzikieuqrtcn.supabase.co";
const VOICE_SUPABASE_ANON_KEY =
  (import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY as string | undefined) ??
  "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImR2ZGRmdmlna3ppa2lldXFydGNuIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NzY4NzAxMTgsImV4cCI6MjA5MjQ0NjExOH0.DiSDka0k9Ig6jYjC1xiXfgiQO6KPNYh5BTUGfeZbfc8";
const FUNCTION_URL = `${VOICE_SUPABASE_URL}/functions/v1/eva-voice`;

// ===== Selectable voices =====
// Keep in sync with the edge function allowlist + the picker UI.
export type EvaVoiceOption = { id: string; label: string; note?: string };
export const EVA_VOICES: EvaVoiceOption[] = [
  { id: "ZsaQ3cfo8U9eHdd0aMDs", label: "Eva (Default)", note: "How Eva built herself" },
  { id: "ujoCPuNXFKVxZSRRrMHv", label: "Casual" },
  { id: "3vbrfmIQGJrswxh7ife4", label: "Posh" },
  { id: "v1IIiVAN4yJaGycxWmjU", label: "Soft" },
  { id: "Iv7T7tshr4OkDgK2zRAo", label: "GenZ" },
  { id: "fATgBRI8wg5KkDFg8vBd", label: "James", note: "Male · neutral" },
  { id: "7p1Ofvcwsv7UBPoFNcpI", label: "Alfred", note: "Male · butler" },
  { id: "RsXsoVCi2tuu2VMRHXGq", label: "JARVIS", note: "Male · AI assistant" },
];
const DEFAULT_VOICE_ID = "ZsaQ3cfo8U9eHdd0aMDs"; // Eva (Default) is the first-load voice
const VOICE_STORAGE_KEY = "eva.voiceId";

let activeVoiceId: string = (() => {
  if (typeof window === "undefined") return DEFAULT_VOICE_ID;
  try {
    const stored = window.localStorage.getItem(VOICE_STORAGE_KEY);
    if (stored && EVA_VOICES.some((v) => v.id === stored)) return stored;
  } catch {}
  return DEFAULT_VOICE_ID;
})();
const voiceListeners = new Set<(id: string) => void>();
function notifyVoice() { voiceListeners.forEach((l) => l(activeVoiceId)); }
export function getVoiceId(): string { return activeVoiceId; }
export function setVoiceId(next: string): void {
  if (!EVA_VOICES.some((v) => v.id === next)) return;
  if (next === activeVoiceId) return;
  activeVoiceId = next;
  try { window.localStorage.setItem(VOICE_STORAGE_KEY, next); } catch {}
  // Stop any in-flight clip so the old voice doesn't keep playing.
  stopCurrent();
  notifyVoice();
}

// Module-level singletons so only one clip plays at a time.
// URLs come from Lovable Cloud Storage (CDN-cached, permanent), so the browser
// handles re-play caching natively — we just memoize the URL per clipId.
// NOTE: cache + in-flight + failure keys are now `${voiceId}::${clipId}` so
// switching voices doesn't serve stale URLs from another voice.
const urlCache = new Map<string, string>();
const inFlight = new Map<string, Promise<string>>();
const failureState = new Map<string, { count: number; lockedUntil: number }>();
const FAILURE_LIMIT = 2;
const LOCKOUT_MS = 30_000;
const lockoutListeners = new Set<() => void>();
function notifyLockout() { lockoutListeners.forEach((l) => l()); }
function clipKey(clipId: string, voiceId: string = activeVoiceId): string {
  return `${voiceId}::${clipId}`;
}

// P31.2 — Health probe singleton. Keep this client-only and non-networked so
// a backend voice misconfiguration never causes a page-load runtime overlay.
// Default to `true` so chips render immediately on first paint; if a real
// fetch fails the per-clip lockout state still gates retries gracefully.
let voiceServiceAvailable: boolean | null = true;
let probeKicked = false;
const probeListeners = new Set<() => void>();
function notifyProbe() { probeListeners.forEach((l) => l()); }
function kickProbe() {
  if (probeKicked || typeof window === "undefined") return;
  probeKicked = true;
  voiceServiceAvailable = true;
  notifyProbe();
}

let currentAudio: HTMLAudioElement | null = null;
let currentClipId: string | null = null;
const listeners = new Set<(playingId: string | null) => void>();

function notify(id: string | null) {
  currentClipId = id;
  listeners.forEach((l) => l(id));
}

export function stopCurrent() {
  if (currentAudio) {
    try {
      currentAudio.pause();
      currentAudio.currentTime = 0;
    } catch {}
    currentAudio = null;
  }
  notify(null);
}

async function fetchClipUrl(clipId: string): Promise<string> {
  const voiceId = activeVoiceId;
  const key = clipKey(clipId, voiceId);
  const cached = urlCache.get(key);
  if (cached) return cached;
  const existing = inFlight.get(key);
  if (existing) return existing;

  const promise = (async () => {
    const res = await fetch(FUNCTION_URL, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        apikey: VOICE_SUPABASE_ANON_KEY,
        Authorization: `Bearer ${VOICE_SUPABASE_ANON_KEY}`,
      },
      body: JSON.stringify({ clipId, voiceId }),
    });
    let data: { url?: string; error?: string; detail?: string } = {};
    try { data = await res.json(); } catch { /* non-JSON body */ }
    if (!res.ok) {
      const detail = data?.error || data?.detail || `status ${res.status}`;
      throw new Error(`TTS failed: ${detail}`);
    }
    if (!data.url) throw new Error(data.error || "No URL returned");
    urlCache.set(key, data.url);
    return data.url;
  })();

  inFlight.set(key, promise);
  try {
    const url = await promise;
    failureState.delete(key);
    notifyLockout();
    return url;
  } catch (err) {
    const prev = failureState.get(key)?.count ?? 0;
    const count = prev + 1;
    failureState.set(key, {
      count,
      lockedUntil: count >= FAILURE_LIMIT ? Date.now() + LOCKOUT_MS : 0,
    });
    notifyLockout();
    throw err;
  } finally {
    inFlight.delete(key);
  }
}

type Props = {
  clipId: string;
  size?: "sm" | "md";
  label?: string;
  className?: string;
  style?: React.CSSProperties;
};

export function EvaPlayButton({ clipId, size = "sm", label = "Play Eva's voice", className, style }: Props) {
  const [playing, setPlaying] = useState(currentClipId === clipId);
  const [loading, setLoading] = useState(false);
  const [lockedUntil, setLockedUntil] = useState<number>(failureState.get(clipKey(clipId))?.lockedUntil ?? 0);
  const [, setTick] = useState(0);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    kickProbe();
    const onChange = (id: string | null) => {
      if (mountedRef.current) setPlaying(id === clipId);
    };
    const onLockout = () => {
      if (mountedRef.current) setLockedUntil(failureState.get(clipKey(clipId))?.lockedUntil ?? 0);
    };
    const onProbe = () => {
      if (mountedRef.current) setTick((n) => n + 1);
    };
    const onVoice = () => {
      // Voice swap → re-evaluate lockout (per-key) and force a tick.
      if (!mountedRef.current) return;
      setLockedUntil(failureState.get(clipKey(clipId))?.lockedUntil ?? 0);
      setTick((n) => n + 1);
    };
    listeners.add(onChange);
    lockoutListeners.add(onLockout);
    probeListeners.add(onProbe);
    voiceListeners.add(onVoice);
    return () => {
      mountedRef.current = false;
      listeners.delete(onChange);
      lockoutListeners.delete(onLockout);
      probeListeners.delete(onProbe);
      voiceListeners.delete(onVoice);
    };
  }, [clipId]);

  // Tick once a second while locked so the button auto-unlocks in the UI.
  useEffect(() => {
    if (!lockedUntil || lockedUntil <= Date.now()) return;
    const id = setInterval(() => setTick((n) => n + 1), 1000);
    return () => clearInterval(id);
  }, [lockedUntil]);

  const serviceDown = voiceServiceAvailable === false;
  const isLocked = serviceDown || lockedUntil > Date.now();
  const remainingS = isLocked ? Math.ceil((lockedUntil - Date.now()) / 1000) : 0;

  const dim = size === "md" ? 28 : 22;
  const iconSize = size === "md" ? 11 : 9;

  const handleClick = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    // Hard guards: ignore clicks while loading, locked, or already playing this clip's start.
    if (loading || isLocked) return;
    // If the probe is still in flight, refuse the click rather than risk a misfire.
    if (voiceServiceAvailable === null) return;

    if (playing) {
      stopCurrent();
      return;
    }

    // Stop any other clip first.
    stopCurrent();

    try {
      setLoading(true);
      const url = await fetchClipUrl(clipId);
      if (!mountedRef.current) return;
      setLoading(false);

      const audio = new Audio(url);
      currentAudio = audio;
      notify(clipId);
      audio.onended = () => {
        if (currentAudio === audio) {
          currentAudio = null;
          notify(null);
        }
      };
      audio.onerror = () => {
        if (currentAudio === audio) {
          currentAudio = null;
          notify(null);
        }
      };
      await audio.play();
    } catch (err) {
      console.error("[EvaPlayButton]", err);
      setLoading(false);
      stopCurrent();
      // Refresh local lockout state so UI flips to "!" if we just hit the threshold.
      setLockedUntil(failureState.get(clipKey(clipId))?.lockedUntil ?? 0);
    }
  };

  const ariaLabel = isLocked
    ? serviceDown
      ? "Voice unavailable"
      : `Voice unavailable. Retry in ${remainingS}s`
    : label;

  return (
    <button
      type="button"
      onClick={handleClick}
      aria-label={ariaLabel}
      aria-pressed={playing}
      disabled={loading || isLocked}
      title={isLocked ? (serviceDown ? "voice unavailable" : `voice unavailable, retry in ${remainingS}s`) : undefined}
      className={className}
      style={{
        width: dim,
        height: dim,
        borderRadius: "50%",
        background: isLocked ? "rgba(255,255,255,0.55)" : playing ? "#C4823A" : "rgba(255,255,255,0.92)",
        color: isLocked ? "#9a8772" : playing ? "#fff" : "#C4823A",
        border: isLocked ? "1px solid rgba(154,135,114,0.4)" : "1px solid rgba(196,130,58,0.45)",
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        cursor: loading || isLocked ? "default" : "pointer",
        opacity: isLocked ? 0.7 : 1,
        boxShadow: playing ? "0 0 0 4px rgba(196,130,58,0.18)" : "0 1px 3px rgba(0,0,0,0.08)",
        transition: "background 180ms ease, color 180ms ease, box-shadow 180ms ease, transform 160ms ease",
        padding: 0,
        flexShrink: 0,
        ...style,
      }}
      onMouseEnter={(e) => { if (!isLocked && !loading) (e.currentTarget as HTMLButtonElement).style.transform = "scale(1.06)"; }}
      onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.transform = "scale(1)"; }}
    >
      {isLocked ? (
        <span aria-hidden style={{ fontSize: iconSize + 2, fontWeight: 700, lineHeight: 1 }}>!</span>
      ) : playing ? (
        <span aria-hidden style={{ display: "inline-flex", alignItems: "flex-end", gap: 1.5, height: iconSize + 2 }}>
          <span className="eva-bar" style={{ width: 2, background: "currentColor", borderRadius: 1, height: "60%" }} />
          <span className="eva-bar eva-bar-2" style={{ width: 2, background: "currentColor", borderRadius: 1, height: "100%" }} />
          <span className="eva-bar eva-bar-3" style={{ width: 2, background: "currentColor", borderRadius: 1, height: "75%" }} />
        </span>
      ) : loading ? (
        <span aria-hidden style={{
          width: iconSize, height: iconSize, borderRadius: "50%",
          border: "1.5px solid currentColor", borderTopColor: "transparent",
          animation: "eva-spin 0.7s linear infinite",
        }} />
      ) : (
        <svg width={iconSize} height={iconSize} viewBox="0 0 12 12" fill="currentColor" aria-hidden>
          <path d="M2.5 1.5v9l8-4.5z" />
        </svg>
      )}
      <style>{`
        @keyframes eva-spin { to { transform: rotate(360deg); } }
        @keyframes eva-bars { 0%,100% { transform: scaleY(0.4); } 50% { transform: scaleY(1); } }
        .eva-bar { transform-origin: bottom; animation: eva-bars 0.9s ease-in-out infinite; }
        .eva-bar-2 { animation-delay: 0.15s; }
        .eva-bar-3 { animation-delay: 0.3s; }
        @media (prefers-reduced-motion: reduce) {
          .eva-bar { animation: none !important; transform: scaleY(0.7); }
        }
      `}</style>
    </button>
  );
};

export default EvaPlayButton;

// ===== Shared playback engine exports =====
// Re-used by `<EvaVoiceTap>` so we never have two clips playing at once
// and the lockout/probe state is unified across the page.

export async function playClip(clipId: string): Promise<void> {
  // If the same clip is currently playing, stop it (toggle).
  if (currentClipId === clipId && currentAudio) {
    stopCurrent();
    return;
  }
  stopCurrent();
  const url = await fetchClipUrl(clipId);
  const audio = new Audio(url);
  currentAudio = audio;
  notify(clipId);
  audio.onended = () => {
    if (currentAudio === audio) { currentAudio = null; notify(null); }
  };
  audio.onerror = () => {
    if (currentAudio === audio) { currentAudio = null; notify(null); }
  };
  await audio.play();
}

/**
 * playClipWhenReady — fetches + preloads the clip, then resolves the moment
 * playback actually begins. Lets callers sync UI animations to the real
 * audio start instead of firing them while the network request is in flight.
 */
export async function playClipWhenReady(clipId: string): Promise<void> {
  if (currentClipId === clipId && currentAudio) {
    stopCurrent();
    return;
  }
  stopCurrent();
  const url = await fetchClipUrl(clipId);
  const audio = new Audio(url);
  audio.preload = "auto";
  currentAudio = audio;
  audio.onended = () => {
    if (currentAudio === audio) { currentAudio = null; notify(null); }
  };
  audio.onerror = () => {
    if (currentAudio === audio) { currentAudio = null; notify(null); }
  };
  await new Promise<void>((resolve, reject) => {
    let settled = false;
    const cleanup = () => {
      audio.removeEventListener("canplaythrough", onReady);
      audio.removeEventListener("error", onErr);
    };
    const onReady = () => {
      if (settled) return;
      settled = true;
      cleanup();
      audio.play().then(() => {
        notify(clipId);
        resolve();
      }).catch(reject);
    };
    const onErr = () => {
      if (settled) return;
      settled = true;
      cleanup();
      reject(new Error("audio failed to load"));
    };
    audio.addEventListener("canplaythrough", onReady, { once: true });
    audio.addEventListener("error", onErr, { once: true });
    // Safety timeout — if canplaythrough never fires (some mobile browsers),
    // fall back to playing whatever is buffered after 2.5s.
    window.setTimeout(() => {
      if (settled) return;
      onReady();
    }, 2500);
    audio.load();
  });
}

export function useVoiceServiceAvailable(): boolean | null {
  const [available, setAvailable] = useState(voiceServiceAvailable);
  useEffect(() => {
    kickProbe();
    const onProbe = () => setAvailable(voiceServiceAvailable);
    probeListeners.add(onProbe);
    return () => { probeListeners.delete(onProbe); };
  }, []);
  return available;
}

export function usePlayingClipId(): string | null {
  const [id, setId] = useState(currentClipId);
  useEffect(() => {
    const onChange = (next: string | null) => setId(next);
    listeners.add(onChange);
    return () => { listeners.delete(onChange); };
  }, []);
  return id;
}

/**
 * useEvaVoice — subscribe to the active Eva voice id and update it.
 * Persists in localStorage; switching voices stops any playing clip.
 */
export function useEvaVoice(): {
  voiceId: string;
  setVoiceId: (id: string) => void;
  voices: typeof EVA_VOICES;
} {
  const [voiceId, setVoiceIdState] = useState<string>(activeVoiceId);
  useEffect(() => {
    const onVoice = (id: string) => setVoiceIdState(id);
    voiceListeners.add(onVoice);
    return () => { voiceListeners.delete(onVoice); };
  }, []);
  return { voiceId, setVoiceId, voices: EVA_VOICES };
}